<script setup>
import { computed, ref } from 'vue'
import { useStore } from '@composables/use-store'
import formatDate from '@utils/format-date-full'
import MixOptions from '@components/mix/mix-options'
import GenrePills from '@components/genre/genre-pills'

const SINGLE_DIGIT_LIMIT = 9
const VISIBLE_GENRES = 2

const props = defineProps({
    index: {
        type: Number,
        default: -1,
    },
    mix: {
        type: Object,
        default: () => {},
    },
    hasLinkToArtistPage: {
        type: Boolean,
        default: false,
    },
    buttons: {
        type: Object,
        default: () => {},
    },
    isIndexedList: {
        type: Boolean,
        default: false,
    },
    genres: {
        type: Boolean,
        default: false,
    },
})

const store = useStore()

const isTileHovered = ref(false)

const airDate = computed(() => {
    if (props.mix.episode) {
        return formatDate(new Date(props.mix.episode.air_start))
    }
    return ''
})

const isMixLoaded = computed(
    () => store.state.playback.resource && props.mix.id === store.state.playback.resource.id
)

const playbackStatus = computed(
    () => store.state.playback.status === 'playing' || store.state.playback.status === 'loading'
)

const tileIndex = computed(() =>
    props.index + 1 <= SINGLE_DIGIT_LIMIT ? `0${props.index + 1}` : props.index + 1
)
</script>

<template>
    <div
        :class="['mix-table-tile', { 'mix-table-tile-active': isMixLoaded && playbackStatus }]"
        @mouseover="isTileHovered = true"
        @mouseleave="isTileHovered = false"
    >
        <div class="inner main">
            <div
                v-if="isIndexedList"
                class="inner index"
            >
                <div class="index-main">
                    {{ tileIndex }}
                </div>
            </div>
            <BaseResourceImage
                :resource="mix.episode"
                class="image"
                :playable-mix="mix"
                :has-hover-state="isTileHovered"
                radius="list"
            />
            <BaseLink
                v-if="mix.artist"
                :to="{ path: `/artist/${mix.artist.url}/${mix.artist.id}/` }"
            >
                <div class="show">
                    {{ mix.artist.title }}
                </div>
            </BaseLink>
            <BasePlaceholder
                v-else
                class="show"
                :text="mix.artist && mix.artist.title"
                type="artist"
            />
        </div>
        <div
            v-if="!hasLinkToArtistPage"
            :class="['inner', 'artist', genres && 'artist-genres']"
        >
            <div class="inner">
                <BaseLink
                    v-if="mix.show && mix.show.title"
                    :to="{ name: 'show', params: { id: mix.show.id } }"
                >
                    {{ mix.show.title }}
                </BaseLink>
                <BasePlaceholder
                    v-else
                    :text="mix.show && mix.show.title"
                    type="show"
                />
            </div>
        </div>
        <div
            v-if="genres"
            class="genre-list"
        >
            <div class="genres">
                <GenrePills
                    align="end"
                    :genres="mix.genre.slice(0, VISIBLE_GENRES)"
                />
            </div>
            <BaseButton
                v-if="mix.genre.length > VISIBLE_GENRES"
                button-type="tagSecondary"
                size="small"
            >
                +{{ mix.genre.length - VISIBLE_GENRES }} MORE
            </BaseButton>
        </div>
        <div class="inner time">
            <div class="inner">
                <BaseLink
                    v-if="mix.title"
                    :to="{ name: 'mix', params: { id: mix.id } }"
                >
                    {{ airDate }}
                </BaseLink>
                <BasePlaceholder
                    v-else
                    :text="mix.title"
                    :size="10"
                />
            </div>
        </div>
        <div class="inner buttons">
            <BaseButton
                v-if="mix && buttons && buttons.playCount"
                class="button button-play-count"
                icon="play_arrow"
                size="small"
                button-type="tag"
            >
                {{ Number(mix.reach).toLocaleString() || '0' }}
            </BaseButton>
            <BaseDropdown class="button">
                <BaseButton
                    slot="trigger"
                    :modifiers="{ autoWidth: true, transparent: true, dots: true }"
                    button-type="tag"
                    aria-label="Options"
                    size="small"
                />
                <div slot="dropdown">
                    <MixOptions :mix="mix" />
                </div>
            </BaseDropdown>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@import '@design';

.main {
    @extend %contentTitleM;

    width: span(7 wide of 12 narrow);
    min-width: span(7 wide of 12 narrow);

    @include tablet {
        width: span(4 narrow of 12 narrow);
        min-width: span(4 narrow of 12 narrow);
    }
}

.mix-table-tile {
    @extend %contentSubtitleM;

    display: flex;
    align-items: center;
    padding: $s-xs;
    border-bottom: 1px solid rgba($c-ebony-clay, 0.35);
    &:hover {
        background: $c-hover-bg;
    }
    &.mix-table-tile-active {
        background: $c-hover-bg;
        .main {
            color: $c-highlight;
        }
    }
    &:active {
        background: $c-active-bg;
    }
}

.inner {
    display: flex;
    align-items: center;
}

.index {
    width: span(2 wide of 12 narrow);
    min-width: span(2 wide of 12 narrow);
    max-width: 70px;
    margin-right: gutter(of 12 narrow);

    &-main {
        color: $c-shuttle-gray;
    }

    @include tablet {
        width: span(2 narrow of 12 narrow);
    }

    @include desktop {
        width: span(1 narrow of 12 narrow);
        min-width: span(1 narrow of 12 narrow);
    }
}

.image {
    display: none;
    width: 2rem;
    height: 2rem;
    margin-right: gutter(of 12 narrow);

    @include tablet {
        display: block;
    }
}

.show {
    color: $c-white;
}

.artist {
    a {
        color: $c-white;
    }

    @include desktop {
        justify-content: flex-end;
        margin-right: gutter(of 12 narrow);
        text-align: right;
    }
}

.artist-genres {
    @include desktop {
        width: span(2 narrow of 12 narrow);
    }
}

.genre-list {
    display: none;

    @include desktop {
        display: flex;
        justify-content: flex-end;
        width: span(4 narrow of 12 narrow);
        max-width: span(4 narrow of 12 narrow);
        overflow: visible;
    }
}

.genres {
    margin-left: $s-m;

    @include desktop {
        margin-left: 0;
    }
}

.time {
    display: none;
    justify-content: flex-end;
    min-width: 10rem;
    margin-left: auto;
    color: $c-shuttle-gray;

    @include tablet {
        display: flex;
    }
}

.buttons {
    display: flex;
    margin-left: auto;

    @include desktop {
        margin-left: gutter(of 12 narrow);
    }
}

.button {
    margin-left: $s-s;

    @include desktop {
        margin-left: 0;
    }
}

.button + .button {
    margin-left: $s-s;
}

.button.button-play-count {
    display: none;

    @include desktop {
        display: block;
    }
}
</style>
